import {
  METRIC_AXES,
  METRIC_RESOLUTION_MS,
  METRIC_GRAPH_LIMITS,
  METRIC_GRAPH_VERSION,
  metricIdentity,
  seriesIdentity,
  spanOf,
  type MetricAxis,
  type MetricDimensions,
  type MetricGraph,
  type MetricQuery,
  type MetricTimeRange,
  type MetricUnit,
} from '@mini-cloud/shared';
import { conversionFactor } from '@/lib/metric-units';

/** The rules the metrics page edits a graph by, kept apart from the page so they can be tested. */

/** Where a new graph starts: no series, and CloudWatch's default window. */
export const EMPTY_GRAPH: MetricGraph = { version: METRIC_GRAPH_VERSION, queries: [], range: { kind: 'relative', durationMs: 3 * METRIC_RESOLUTION_MS['1h'] } };

/** The first `m<n>` no query has, so ids stay short and read in the order they were added. */
export function nextQueryId(queries: ReadonlyArray<MetricQuery>): string {
  const taken = new Set(queries.map((query) => query.id));
  let candidate = 1;
  while (taken.has(`m${candidate}`)) {
    candidate += 1;
  }
  return `m${candidate}`;
}

/** The first colour nobody holds, or `undefined` when all of them are taken. Never cycled. */
function firstFreeColor(taken: ReadonlySet<number>): number | undefined {
  for (let candidate = 1; candidate <= METRIC_GRAPH_LIMITS.colors; candidate += 1) {
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

/** Each query's colour: its own when it has one, otherwise the first nobody holds. */
export function colorsOf(queries: ReadonlyArray<MetricQuery>): ReadonlyArray<number> {
  const taken = new Set(queries.flatMap((query) => (query.color === undefined ? [] : [query.color])));
  return queries.map((query) => {
    if (query.color !== undefined) {
      return query.color;
    }
    // A graph with no colour left is one the validator refuses, so it can only be a
    // graph being built: the last colour draws it rather than nothing drawing it.
    const color = firstFreeColor(taken) ?? METRIC_GRAPH_LIMITS.colors;
    taken.add(color);
    return color;
  });
}

/** The colour a new query is given, written into it so it keeps that colour. */
export function nextColor(queries: ReadonlyArray<MetricQuery>): number | undefined {
  return firstFreeColor(new Set(colorsOf(queries)));
}

/** How a dimension set reads in a picker or a row. The empty set is a series too. */
export function describeDimensions(dimensions: MetricDimensions): string {
  const entries = Object.entries(dimensions);
  if (entries.length === 0) {
    return 'No dimensions';
  }
  return entries.map(([name, value]) => `${name}=${value}`).join(', ');
}

/** Whether two queries read the same metric and dimension set, whatever statistic each takes. */
export function isSameMetric(left: MetricQuery, right: MetricQuery): boolean {
  return metricIdentity(left) === metricIdentity(right);
}

/** Whether two queries read the same series, and so would draw one line twice. */
export function isSameSeries(left: MetricQuery, right: MetricQuery): boolean {
  return seriesIdentity(left) === seriesIdentity(right);
}

/** Whether `candidate` would read a series the graph already has. */
export function isOnGraph(queries: ReadonlyArray<MetricQuery>, candidate: MetricQuery): boolean {
  return queries.some((query) => isSameSeries(query, candidate));
}

/** "CpuUtilization · nas · p99": the metric, its dimension values, and the statistic. */
export function labelOf(query: MetricQuery): string {
  return query.label ?? [query.metricName, ...Object.values(query.dimensions), query.statistic].join(' · ');
}

export type AxisUnits = Readonly<Record<MetricAxis, ReadonlyArray<MetricUnit>>>;

export interface PlacedUnit {
  readonly axis: MetricAxis;
  readonly unit: MetricUnit;
}

/** The kinds of unit on each axis, one entry per group that converts into itself, as the chart groups them. */
export function axisUnitsOf(placed: ReadonlyArray<PlacedUnit>): AxisUnits {
  const units: Record<MetricAxis, MetricUnit[]> = { left: [], right: [] };
  for (const { axis, unit } of placed) {
    if (!units[axis].some((existing) => conversionFactor(unit, existing) !== undefined)) {
      units[axis].push(unit);
    }
  }
  return units;
}

/** Whether a series in `unit` can be read against `axis` without mixing its scale. */
export function axisAccepts(unit: MetricUnit, axis: MetricAxis, units: AxisUnits): boolean {
  return units[axis].every((existing) => conversionFactor(unit, existing) !== undefined);
}

/**
 * The axis a new series in `unit` belongs on: the one already holding its kind of
 * unit, else an empty one, left first. `undefined` when both hold other units, since a
 * third would have no scale to be read against.
 */
export function axisFor(unit: MetricUnit, units: AxisUnits): MetricAxis | undefined {
  return METRIC_AXES.find((axis) => units[axis].length > 0 && axisAccepts(unit, axis, units)) ?? METRIC_AXES.find((axis) => units[axis].length === 0);
}

/** Whether a range holds at least one whole bucket of a period; one that does not reads nothing. */
export function periodFits(spanMs: number, periodMs: number): boolean {
  return periodMs <= spanMs;
}

/** More points than a plot the width of the console has pixels, by a margin: a day by the minute. */
const DENSE_POINTS = 1440;

/**
 * How many points a period draws over a span, when that is enough to be worth saying
 * before someone picks it. `undefined` when it is not.
 */
export function densePointCount(spanMs: number, periodMs: number): number | undefined {
  const points = Math.ceil(spanMs / periodMs);
  return points > DENSE_POINTS ? points : undefined;
}

/**
 * The graph over a new range. A chosen period longer than the new range is dropped, so
 * the graph falls back to the automatic one instead of reading nothing.
 */
export function withRange(graph: MetricGraph, range: MetricTimeRange): MetricGraph {
  const keepPeriod = graph.periodMs !== undefined && periodFits(spanOf(range), graph.periodMs);
  return { ...graph, range, periodMs: keepPeriod ? graph.periodMs : undefined };
}
